// World export (M29). Reads a saved world out of IndexedDB and renders it to a
// portable `.tfworld` file (see `sim/save/worldfile.ts`) so it can be handed to a
// headless server via `tools/import-world.ts`. Only edited sections are carried —
// pristine terrain regenerates from the seed on the host — so a build exports small.
// The durable store has no live world clock, so exports carry `clock: null` (the
// host starts the imported world at its own tick 0).
import type { WorldFileStore } from './saves';
import { serializeWorldFile, type WorldFile, type WorldFileMeta, type WorldFileSection, type WorldFileBlockEntity, type WorldFileMob } from '../../sim/save/worldfile';

const HOST_DIM = 'overworld'; // the one dimension a v1.1 host serves

/** Build the `.tfworld` JSONL text for a saved world (its overworld sections +
 *  block entities — M51.6, D-TF-257: a lesson template needs a stocked chest). */
export async function exportWorldFile(save: WorldFileStore, id: string): Promise<string> {
  const meta = await save.getWorld(id);
  if (!meta) throw new Error(`export: no world '${id}'`);
  const fileMeta: WorldFileMeta = {
    id: meta.id, name: meta.name, seed: meta.seed, worldType: meta.worldType,
    gameMode: meta.gameMode, difficulty: meta.difficulty, hardcore: !!meta.hardcore,
    flatPreset: meta.flatPreset ?? '',
  };
  const loaded = await save.loadSections(id, HOST_DIM);
  const sections: WorldFileSection[] = loaded.map((s) => ({ dim: HOST_DIM, cx: s.cx, cz: s.cz, sy: s.sy, states: s.states }));
  const beRows = await save.loadBlockEntities(id, HOST_DIM);
  const blockEntities: WorldFileBlockEntity[] = beRows.map((be) => ({ dim: HOST_DIM, x: be.x, y: be.y, z: be.z, type: be.type, data: be.data }));
  // M53.5 (D-TF-265): a mob row is a SPAWN DECLARATION, not a full entity snapshot —
  // exactly enough for a template to mean anything on reimport (position/health/baby/
  // genome). Velocity/age/tame state etc. are not carried; a re-imported mob is a
  // fresh spawn at that position, matching a lesson's own inline-template mobs.
  const mobRows = await save.loadEntities(id, HOST_DIM);
  const mobs: WorldFileMob[] = mobRows.map((e) => ({
    dim: HOST_DIM, x: e.x, y: e.y, z: e.z, type: e.type, health: e.health, baby: e.baby,
    ...(e.genome !== undefined ? { genome: e.genome } : {}),
  }));
  const file: WorldFile = { meta: fileMeta, clock: null, sections, blockEntities, mobs };
  return serializeWorldFile(file);
}

/** Trigger a browser download of the exported text as `<name>.tfworld`. */
export function downloadWorldFile(text: string, name: string): void {
  const blob = new Blob([text], { type: 'application/x-ndjson' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.tfworld`;
  a.click();
  URL.revokeObjectURL(url);
}
